import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { ApiServiceService } from '../../services/common/api-service.service';
import { SubscriptionService } from '../../services/subscription/subscription.service';
import { Subscription } from 'rxjs';
import Swal from 'sweetalert2';
import { MatTabGroup } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';

interface ProfileData {
  personal_info: any;
  basic_info: any;
  interest_status: string;
  _id: string;
  interest_owner_id: any;
  selected_profile_id: string;
}

interface SenderData {
  _id: string;
  interest_owner_id: string;
  selected_profile_id: any;
  interest_status: string;
}

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit, OnDestroy {
  likes = 12;
  views = 28;
  interests = 8;
  clicks = 45;

  @ViewChild('tabGroup') tabGroup!: MatTabGroup;
  
  newRequests: ProfileData[] = [];
  acceptedRequests: ProfileData[] = [];
  deniedRequests: ProfileData[] = [];
  dataList: SenderData[] = [];
  favoritedProfileIds = new Set<string>();
  base64Images: { [oryId: string]: string } = {};
  private favoritesSubscription?: Subscription;
  showSenderRequestsTab: boolean = false;
  filteredMatches: any[] = [];
  displayMatches: any[] = []; // Used for local filtering on the UI
  preferenceFilter: any;
  loading = true;

  // Eternaltie Variables
  searchQuery: string = '';
  selectedAgeGroup: string = 'All';
  selectedReligion: string = 'All';
  selectedLocation: string = 'All';
  showAdvanced: boolean = false;
  selectedDiet: string = 'All';
  selectedEducation: string = 'All';
  selectedGender: string = 'All';

  hasPreferences: boolean = true;
  cities: any[] = [];
  religions: any[] = [];
  educations: any[] = [];
  incomes: any[] = [];
  occupations: any[] = [];
  languages: any[] = [];

  loggedInGender: string = '';
  currentPage: number = 1;
  pageSize: number = 10;
  hasSearched: boolean = false;
  isBasicSearchActive: boolean = false;
  recommendedMatches: any[] = [];
  profilesViewedToday = 0;

  get paginatedMatches() {
    return this.displayMatches.slice(0, this.currentPage * this.pageSize);
  }

  loadMore() {
    this.currentPage++;
  }

  showLess() {
    if (this.currentPage > 1) {
      this.currentPage--;
    }
  }

  currentSubscription: any = null;
  boostingInProgress: boolean = false;

  // Boost status state
  boostStatus: any = null;
  boostCountdownLabel: string = '';
  private boostCountdownInterval: any = null;

  constructor(
    private apiService: ApiServiceService,
    public subscriptionService: SubscriptionService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.loading = true;
    this.loadMasterData();
    this.getReceivedConnectionListData();
    this.getSentConnectionListData();
    this.loadLoggedInUserDetailsAndPreferences();
    this.loadFavoritesCount();

    this.favoritesSubscription = this.apiService.favoritesChanged.subscribe(evt => {
      if (evt.action === 'add') {
        this.favoritedProfileIds.add(evt.profileId);
      } else {
        this.favoritedProfileIds.delete(evt.profileId);
      }
      this.likes = this.favoritedProfileIds.size;
    });

    const userId = localStorage.getItem('profileId') || localStorage.getItem('login_id') || localStorage.getItem('user_id') || '406830588667355136';
    this.subscriptionService.fetchCurrentSubscription(userId).subscribe((sub) => {
      this.currentSubscription = sub;
      this.loadBoostStatus(userId);
    });

    setTimeout(() => {
      this.loading = false;
    }, 1000);
  }

  ngOnDestroy(): void {
    this.clearBoostCountdown();
    this.favoritesSubscription?.unsubscribe();
  }

  loadBoostStatus(userId: string): void {
    this.subscriptionService.getBoostStatus(userId).subscribe({
      next: (res) => {
        if (res?.status === 'success') {
          this.boostStatus = res.data;
          if (this.boostStatus.boostActive) {
            this.startBoostCountdown();
          }
        }
      },
      error: () => { this.boostStatus = null; }
    });
  }

  private startBoostCountdown(): void {
    this.clearBoostCountdown();
    this.updateCountdownLabel();
    this.boostCountdownInterval = setInterval(() => {
      this.updateCountdownLabel();
      if (!this.isBoostActive()) {
        this.clearBoostCountdown();
        const userId = localStorage.getItem('profileId') || '';
        if (userId) this.loadBoostStatus(userId);
      }
    }, 1000);
  }

  private updateCountdownLabel(): void {
    if (!this.boostStatus?.boostUntil) { this.boostCountdownLabel = ''; return; }
    const diff = new Date(this.boostStatus.boostUntil).getTime() - Date.now();
    if (diff <= 0) { this.boostCountdownLabel = 'Expiring...'; return; }
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    this.boostCountdownLabel = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }

  private clearBoostCountdown(): void {
    if (this.boostCountdownInterval) {
      clearInterval(this.boostCountdownInterval);
      this.boostCountdownInterval = null;
    }
  }

  getCooldownLabel(): string {
    if (!this.boostStatus?.nextBoostAvailableAt) return '';
    const diff = new Date(this.boostStatus.nextBoostAvailableAt).getTime() - Date.now();
    if (diff <= 0) return '';
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return `Available again in ${days} day${days !== 1 ? 's' : ''}`;
  }

  getBoostsRemaining(): number {
    if (!this.boostStatus) return 0;
    return Math.max(0, (this.boostStatus.boostMonthlyLimit || 0) - (this.boostStatus.boostsUsedThisMonth || 0));
  }

  triggerProfileBoost(): void {
    const userId = localStorage.getItem('profileId') || localStorage.getItem('login_id') || localStorage.getItem('user_id') || '406830588667355136';
    this.boostingInProgress = true;
    this.subscriptionService.boostProfile(userId).subscribe({
      next: (res) => {
        this.boostingInProgress = false;
        Swal.fire({
          title: '🚀 Profile Boost Active!',
          text: 'Your profile will appear at the top of compatible discovery listings for 24 hours!',
          icon: 'success',
          confirmButtonColor: '#800020'
        });
        this.loadBoostStatus(userId);
      },
      error: (err) => {
        this.boostingInProgress = false;
        this.apiService.handleQuotaError(err);
      }
    });
  }

  isBoostActive(): boolean {
    if (this.boostStatus?.boostActive) return true;
    if (!this.currentSubscription?.boost_until) return false;
    return new Date(this.currentSubscription.boost_until) > new Date();
  }

  loggedInProfile: any = null;

  get hasUploadedHoroscope(): boolean {
    return !!(this.loggedInProfile && this.loggedInProfile.horoscope_file_url);
  }

  get hasAddedGalleryPhotos(): boolean {
    return !!(this.loggedInProfile && this.loggedInProfile.personal_info && Array.isArray(this.loggedInProfile.personal_info.gallery_photos) && this.loggedInProfile.personal_info.gallery_photos.length > 0);
  }

  loadLoggedInUserDetailsAndPreferences() {
    const sessionToken = localStorage.getItem('sessionToken');
    const profileId = localStorage.getItem('profileId');
    if (sessionToken && profileId) {
      this.apiService.getEditDetails(sessionToken, profileId).subscribe(res => {
        if (res && res.data) {
          this.loggedInProfile = res.data;
          this.profilesViewedToday = Number(res.data.profile_views_today || 0);
          if (res.data.basic_info) {
            this.loggedInGender = res.data.basic_info.gender;
          }
        }
        this.checkPartnerPreferences();
      }, err => {
        console.error('Error fetching logged in details', err);
        this.checkPartnerPreferences();
      });
    } else {
      this.checkPartnerPreferences();
    }
  }

  checkPartnerPreferences() {
    const sessionToken = localStorage.getItem('sessionToken');
    const profileId = localStorage.getItem('profileId');
    if (sessionToken && profileId) {
      this.apiService.getPartnerDetails(sessionToken, profileId).subscribe(
        (response) => {
          if (response.status === 'success' && response.data && typeof response.data === 'object' && Object.keys(response.data).length > 0) {
            this.hasPreferences = true;
            this.getpreferenceFilters();
          } else {
            this.hasPreferences = false;
            this.fetchBasicMatches();
            
            const skipped = sessionStorage.getItem('skippedPreferences');
            const redirected = sessionStorage.getItem('redirectedToPreferences');
            
            if (skipped !== 'true' && redirected !== 'true') {
              sessionStorage.setItem('redirectedToPreferences', 'true');
              this.router.navigate(['/partner-preferences']);
            }
          }
        },
        (error) => {
          console.error('Error fetching partner details:', error);
          this.hasPreferences = false;
          this.fetchBasicMatches();
        }
      );
    }
  }

  loadMasterData() {
    this.apiService.getCity().subscribe(res => { if(res.data) this.cities = res.data; });
    this.apiService.getReligions().subscribe(res => { if(res.data) this.religions = res.data; });
    this.apiService.getEducation().subscribe(res => { if(res.data) this.educations = res.data; });
    this.apiService.getIncome().subscribe(res => { if(res.data) this.incomes = res.data; });
    this.apiService.getOccupation().subscribe(res => { if(res.data) this.occupations = res.data; });
    this.apiService.getLanguages().subscribe(res => { if(res.data) this.languages = res.data; });
  }

  getCityName(id: string): string {
    if (!id) return '--';
    const match = this.cities.find(c => c._id === id);
    return match ? match.city_nme : id;
  }

  getReligionName(id: string): string {
    if (!id) return '--';
    const match = this.religions.find(r => r._id === id);
    return match ? match.religion_nme : id;
  }

  getEducationName(id: string): string {
    if (!id) return '--';
    const match = this.educations.find(e => e._id === id);
    return match ? match.education_nme : id;
  }

  getIncomeName(id: string): string {
    if (!id) return '--';
    const match = this.incomes.find(i => i._id === id);
    return match ? match.income_nme : id;
  }

  getOccupationName(id: any): string {
    if (!id) return 'Professional';
    if (typeof id === 'object' && id.occupation_nme) return id.occupation_nme;
    const match = this.occupations.find(o => o._id === id);
    return match ? match.occupation_nme : 'Professional';
  }

  getLanguageName(id: any): string {
    if (!id) return '--';
    let langId = id;
    if (Array.isArray(id) && id.length > 0) langId = id[0];
    if (typeof langId === 'object' && langId.language_nme) return langId.language_nme;
    const match = this.languages.find(l => l._id === langId);
    return match ? match.language_nme : langId;
  }

  fetchBasicMatches() {
    const sessionToken = localStorage.getItem('sessionToken');
    const profileId = localStorage.getItem('profileId');
    if (!sessionToken || !profileId) return;

    const filterData: any = {};
    
    if (!filterData.personal_filter) {
      filterData.personal_filter = {};
    }

    if (this.selectedAgeGroup !== 'All') {
      let minAge = 18; let maxAge = 60;
      if (this.selectedAgeGroup === '21-25') { minAge = 21; maxAge = 25; }
      else if (this.selectedAgeGroup === '26-28') { minAge = 26; maxAge = 28; }
      else if (this.selectedAgeGroup === '29+') { minAge = 29; maxAge = 60; }
      filterData.personal_filter.age_from = minAge;
      filterData.personal_filter.age_to = maxAge;
    }

    if (this.selectedReligion !== 'All') {
      filterData.religious_filter = { religion_id: this.selectedReligion };
    }

    if (this.selectedLocation !== 'All') {
      filterData.location_filter = { city_id: this.selectedLocation };
    }

    // If user explicitly picked a gender, pass it; otherwise let the backend
    // enforce opposite-gender based on the logged-in user's own profile.
    if (this.selectedGender !== 'All') {
      filterData.personal_filter.gender = this.selectedGender;
    }
    // (no else — backend handles opposite-gender enforcement)

    const requestData = {
      action: 'find_all',
      type: 'filter',
      profileId: profileId,
      data: filterData
    };

    this.loading = true;
    this.apiService.getfilterMatches(sessionToken, requestData).subscribe(
      (response) => {
        this.loading = false;
        if (response.status === 'success' && Array.isArray(response.data)) {
          this.preferenceFilter = response.data;
          this.updateFilteredMatches();
          this.filteredMatches.forEach((profile: any) => {
            const idKey = profile.basic_info?.keycloak_id || profile.basic_info?.ory_identity_id || profile._id;
            if (profile.personal_info?.avatar_Id && idKey) {
              const imgUrl = `${idKey}/gallery/avatar/${profile.personal_info.avatar_Id}`;
              this.loadImage(imgUrl, profile._id);
            }
          });
        } else {
          this.preferenceFilter = [];
          this.filteredMatches = [];
          this.displayMatches = [];
        }
      },
      (error) => {
        this.loading = false;
        console.error('Error fetching basic matches:', error);
      }
    );
  }

  getAvatarKey(profile: any): string {
    return profile?.basic_info?.keycloak_id || profile?.basic_info?.ory_identity_id || '';
  }

  getAge(dateOfBirth: string): number {
    if (!dateOfBirth) return 0;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  }

  getpreferenceFilters(): void {
    const sessionToken = localStorage.getItem('sessionToken');
    const profileId = localStorage.getItem('profileId');
    if (sessionToken && profileId) {
      this.apiService.getPreferenceFilter(sessionToken, profileId).subscribe(
        (response) => {
          if (response.status === 'success' && Array.isArray(response.data)) {
            this.recommendedMatches = response.data;

console.log(
  "TOTAL RECOMMENDED PROFILES:",
  this.recommendedMatches.length
);


// temporary: check one specific profile
this.recommendedMatches.forEach((profile:any) => {

  if (
    profile.basic_info?.first_name === "deva"
  ) {

    console.log(
      "CHECKED PROFILE DATA:",
      {
        id: profile._id,

        name:
          profile.basic_info?.first_name +
          " " +
          profile.basic_info?.last_name,

        marital_status:
          profile.personal_info?.marital_status,

        match_badge:
          profile.match_badge,

        preference_fit:
          profile.preference_fit,

        similarity_fit:
          profile.similarity_fit,

        final_score:
          profile.final_score,

        activePreferenceFields:
          profile.active_preference_fields,

        matchedPreferenceFields:
          profile.matched_preference_fields
      }
    );

  }

});
            this.preferenceFilter = response.data;
            this.updateFilteredMatches();
            this.filteredMatches.forEach((profile: any) => {
              const idKey = profile.basic_info?.keycloak_id || profile.basic_info?.ory_identity_id || profile._id;
              if (profile.personal_info?.avatar_Id && idKey) {
                const imgUrl = `${idKey}/gallery/avatar/${profile.personal_info.avatar_Id}`;
                this.loadImage(imgUrl, profile._id);
              }
            });
          } else {
            this.preferenceFilter = [];
            this.filteredMatches = [];
            this.displayMatches = [];
          }
        },
        (error) => {
          console.error('Error fetching partner preferences:', error);
        }
      );
    }
  }

  getCompatibleMatchesCount(): number {
    if (!this.recommendedMatches) return 0;
    return this.recommendedMatches.filter((p: any) => 
      p.match_badge === 'Preference Match' || p.match_badge === 'Strong Match' || p.match_badge === 'Partial Match'
    ).length;
  }

  updateFilteredMatches() {
    if (!this.preferenceFilter) return;
    
    const excludeIds = new Set<string>();
    const profileId = localStorage.getItem('profileId');
    if (profileId) {
      excludeIds.add(profileId);
    }
    
    const addReceived = (reqList: any[]) => {
      reqList?.forEach(req => {
        if (req.interest_owner_id?._id) {
          excludeIds.add(req.interest_owner_id._id);
        }
      });
    };
    
    // Commented out to allow profiles to remain in the Discover list
    // addReceived(this.newRequests);
    // addReceived(this.acceptedRequests);
    // addReceived(this.deniedRequests);
    
    // this.dataList?.forEach(req => {
    //   if (req.selected_profile_id?._id) {
    //     excludeIds.add(req.selected_profile_id._id);
    //   }
    // });

    this.filteredMatches = this.preferenceFilter.filter((profile: any) => !excludeIds.has(profile._id));
    // Reset pagination on new filter
    this.currentPage = 1;
    this.applyLocalFilters();
  }

  // Eternaltie Filtering Logic
  applyLocalFilters() {
    this.displayMatches = this.filteredMatches.filter((p: any) => {
      // Search Query
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        const matchesName = (p.basic_info?.first_name + ' ' + p.basic_info?.last_name).toLowerCase().includes(query);
        const matchesProfession = (p.professional_info?.occupation_id || '').toLowerCase().includes(query);
        const matchesLocation = (p.residing_location?.city_id || '').toLowerCase().includes(query);
        if (!matchesName && !matchesProfession && !matchesLocation) return false;
      }

      return true;
    });
  }

  clearSearchQuery() {
    this.searchQuery = '';
    this.applyLocalFilters();
  }


  triggerBasicSearch() {
    this.hasSearched = true;
    this.isBasicSearchActive = true;
    this.fetchBasicMatches();
  }

  resetToRecommended() {
    if (!this.hasPreferences) return;
    this.isBasicSearchActive = false;
    this.selectedAgeGroup = 'All';
    this.selectedReligion = 'All';
    this.selectedLocation = 'All';
    this.selectedGender = 'All';
    this.searchQuery = '';
    
    // Restore from cached recommended matches to avoid unnecessary API calls
    this.preferenceFilter = [...this.recommendedMatches];
    this.updateFilteredMatches();
  }

  resetAllFilters() {
    this.searchQuery = '';
    this.selectedAgeGroup = 'All';
    this.selectedReligion = 'All';
    this.selectedLocation = 'All';
    this.selectedDiet = 'All';
    this.selectedEducation = 'All';
    this.selectedGender = 'All';
    this.currentPage = 1;
    if (this.hasPreferences) {
      if (this.isBasicSearchActive) {
        this.resetToRecommended();
      } else {
        this.applyLocalFilters();
      }
    } else {
      this.hasSearched = false;
      this.fetchBasicMatches();
    }
  }

  handleSaveFilter() {
    Swal.fire({
      title: 'Save Search Filter',
      input: 'text',
      inputLabel: 'Enter a name for this search filter',
      inputPlaceholder: 'e.g., Creative Professionals, Chennai Doctors',
      showCancelButton: true,
      confirmButtonText: 'Save Filter',
      confirmButtonColor: '#8c002a',
      inputValidator: (value) => {
        if (!value) {
          return 'You need to specify a filter name!';
        }
        return null;
      }
    }).then((result) => {
      if (result.isConfirmed && result.value) {
        Swal.fire({
          icon: 'success',
          title: 'Filter Saved!',
          text: `"${result.value}" is now saved.`,
          confirmButtonColor: '#8c002a',
          timer: 2000
        });
      }
    });
  }

  hasSentInterest(profile: any): boolean {
    return this.dataList.some(req => req.selected_profile_id?._id === profile._id);
  }

  getSentInterestStatus(profile: any): string | null {
    const req = this.dataList.find((r: any) => r.selected_profile_id?._id === profile._id);
    return req ? req.interest_status : null;
  }

  triggerInterestAlert(profile: any) {
    if (this.hasSentInterest(profile)) return;
    this.sendInterest(profile);
  }

  isFavorited(profileId: string): boolean {
    return this.favoritedProfileIds.has(profileId);
  }

  toggleFavorite(profile: any) {
    const sessionToken = localStorage.getItem('sessionToken');
    const ownerId = localStorage.getItem('profileId');
    if (!sessionToken || !ownerId) return;

    if (this.isFavorited(profile._id)) {
      this.apiService.deleteFavorite(sessionToken, profile._id, ownerId).subscribe(() => {
        this.favoritedProfileIds.delete(profile._id);
        this.apiService.favoritesChanged.next({ profileId: profile._id, action: 'remove' });
        this.likes--;
        Swal.fire({
          title: 'Removed!',
          text: 'Profile removed from favorites.',
          icon: 'success',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 1500
        });
      }, err => {
        console.error('Error removing favorite:', err);
      });
    } else {
      this.apiService.addToFavorites(sessionToken, profile._id, ownerId, profile.basic_info || {}, profile.religion_info || {}).subscribe(() => {
        this.favoritedProfileIds.add(profile._id);
        this.apiService.favoritesChanged.next({ profileId: profile._id, action: 'add' });
        this.likes++;
        Swal.fire({
          title: 'Added to Shortlist',
          text: 'Profile added to your favorites.',
          icon: 'success',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 1500
        });
      }, err => {
        if (this.apiService.handleQuotaError(err)) return;
        console.error('Error adding favorite:', err);
      });
    }
  }

  loadFavoritesCount() {
    const sessionToken = localStorage.getItem('sessionToken');
    const ownerId = localStorage.getItem('profileId');
    if (!sessionToken || !ownerId) return;

    this.apiService.getFavorites(sessionToken, ownerId).subscribe(res => {
      if (res.status === 'success' && Array.isArray(res.data)) {
        this.likes = res.data.length;
        this.favoritedProfileIds = new Set(res.data.map((fav: any) => {
          return typeof fav.profile === 'object' && fav.profile ? fav.profile._id : fav.profile;
        }));
      } else {
        this.likes = 0;
        this.favoritedProfileIds.clear();
      }
    }, err => {
      console.error('Error fetching favorites:', err);
      this.likes = 0;
    });
  }

  // Existing Data Methods
  getReceivedConnectionListData(): void {
    const sessionToken = localStorage.getItem('sessionToken');
    const profileId = localStorage.getItem('profileId');
    if (!sessionToken || !profileId) return;

    this.apiService.getReceivedConnectionList(sessionToken, profileId).subscribe(
      (response: { data: ProfileData[] }) => {
        const receivedData = response.data || [];
        this.newRequests = receivedData.filter((request: ProfileData) => request.interest_status === 'Pending');
        this.deniedRequests = receivedData.filter((request: ProfileData) => request.interest_status === 'Denied');
        this.acceptedRequests = receivedData.filter((request: ProfileData) => request.interest_status === 'Accepted');

        receivedData.forEach(request => {
          const owner = request.interest_owner_id;
          if (owner) {
            const idKey = owner.basic_info?.keycloak_id || owner.basic_info?.ory_identity_id || owner._id;
            const avatarId = owner.personal_info?.avatar_Id;
            if (idKey && avatarId) {
              const imagePath = `${idKey}/gallery/avatar/${avatarId}`;
              this.loadImage(imagePath, owner._id);
            }
          }
        });
        this.updateFilteredMatches();
      },
      (error) => {
        console.error('Error fetching received connections:', error);
      }
    );
  }

  loadImage(imagePath: string, keyId: string): void {
    if (this.base64Images[keyId]) return; // already loaded
    this.apiService.getImageBase64(imagePath).subscribe(
      (data) => {
        if (data && data.base64Image) {
          this.base64Images[keyId] = data.base64Image;
        }
      },
      (error) => {
        console.error('Error loading image base64:', error);
      }
    );
  }

  sendInterest(profile: any): void {
    const sessionToken = localStorage.getItem('sessionToken');
    const profileId = localStorage.getItem('profileId');
    if (!sessionToken || !profileId) {
      this.router.navigate(['/login']);
      return;
    }

    this.apiService.setConnectionStatus(sessionToken, profileId, profile._id, 'Pending').subscribe(
      (response) => {
        Swal.fire({
          title: 'Interest Sent!',
          text: `You have successfully sent an interest request to ${profile.basic_info.first_name}.`,
          icon: 'success',
          confirmButtonColor: '#800020'
        });
        this.getSentConnectionListData();
      },
      (error) => {
        if (this.apiService.handleQuotaError(error)) return;
        Swal.fire({
          title: 'Error',
          text: 'Failed to send interest request. Please try again.',
          icon: 'error',
          confirmButtonColor: '#800020'
        });
        console.error('Error sending interest:', error);
      }
    );
  }

  cancelInterest(profile: any): void {
    Swal.fire({
      title: 'Revert Request?',
      text: `Are you sure you want to cancel the interest request sent to ${profile.basic_info?.first_name || 'this profile'}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#800020',
      confirmButtonText: 'Yes, Cancel Request'
    }).then((result) => {
      if (result.isConfirmed) {
        const sessionToken = localStorage.getItem('sessionToken');
        const profileId = localStorage.getItem('profileId');
        if (!sessionToken || !profileId) return;

        this.apiService.setConnectionStatus(sessionToken, profileId, profile._id, 'Cancelled').subscribe(
          (response) => {
            Swal.fire({
              title: 'Cancelled',
              text: 'Your interest request has been reverted.',
              icon: 'success',
              confirmButtonColor: '#800020'
            });
            this.getSentConnectionListData();
          },
          (error) => {
            console.error('Error cancelling interest:', error);
            Swal.fire('Error', 'Could not cancel request. Please try again.', 'error');
          }
        );
      }
    });
  }

  getSentConnectionListData(): void {
    const sessionToken = localStorage.getItem('sessionToken');
    const profileId = localStorage.getItem('profileId');
    if (!sessionToken || !profileId) return;

    this.apiService.getSentConnectionList(sessionToken, profileId).subscribe(
      (response: { data: SenderData[] }) => {
        this.dataList = response.data || [];
        this.showSenderRequestsTab = this.dataList.length > 0;
        this.dataList.forEach(sender => {
          const selected = sender.selected_profile_id;
          if (selected) {
            const idKey = selected.basic_info?.keycloak_id || selected.basic_info?.ory_identity_id || selected._id;
            const avatarId = selected.personal_info?.avatar_Id;
            if (idKey && avatarId) {
              const imagePath = `${idKey}/gallery/avatar/${avatarId}`;
              this.loadImage(imagePath, selected._id);
            }
          }
        });
        this.updateFilteredMatches();
      },
      (error) => {
        console.error('Error fetching sent connection list:', error);
      }
    );
  }

  isFeaturedProfile(profile: any): boolean {
    if (!profile) return false;
    if (profile.isFeatured !== undefined) return Boolean(profile.isFeatured);
    const plan = (profile.current_plan || '').toUpperCase();
    return plan !== 'FREE' && plan !== 'SILVER' && plan !== '';
  }
}
